import IconButton from '@/components/action-button';
import PharmacyCareClinic from '@/components/pharmacy-care-clinic';
import TabComponent from '@/components/tab/tab.component';
import { useViewportType } from '@/hooks';
import { capitalizeEveryFirstLetter } from '@/utils';
import { useTheme } from '@chakra-ui/react';
import { Button } from '@ldfeplatform/componentlibrary.ui.atoms.button';
import { WrapperSection } from '@ldfeplatform/componentlibrary.ui.atoms.section';
import { Text } from '@ldfeplatform/componentlibrary.ui.atoms.text';
import { Alert } from '@ldfeplatform/componentlibrary.ui.molecules.alert';
import { Breadcrumbs } from '@ldfeplatform/componentlibrary.ui.molecules.breadcrumbs';
import { IconType } from '@react-icons/all-files';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { MdErrorOutline } from 'react-icons/md';
import { useIntl } from 'react-intl';
import GoTagSvg from '../../data/icons';
import BeautyBrands from '../beauty-brands';
import PharmacyAndServices from '../pharmacy-and-services';
import StoreInformation from '../store-information/store-information.component';
import { geoLocationOptions, handleGeoLocationError } from '../store-locator/store-locator-utils';
import { STORE_DETAILS_BREADCRUMBS, STORE_TYPES, TAB_STORE_DETAILS } from './constants';
import { IStoreDetails, TLayout, TPharmacistInfo, TStoreDetailsResults } from './store-details.interface';
import styles from './store-details.module.scss';
import { formatDistance, getLocationName, getInitialNavItems } from './utils';

const storeDetailsData = (layout: TLayout) => {
  const pharmacistInfo = layout?.sections?.mainContentCollection?.components?.find(
    ({ componentId }) => componentId === 'pharmacistInfoComponent'
  )?.data as TPharmacistInfo;
  const storeDetails = layout?.sections?.mainContentCollection?.components?.find(
    ({ componentId }) => componentId === 'storeDetailsComponent'
  )?.data as TStoreDetailsResults;

  return {
    pharmacistInfo,
    storeDetails,
  };
};

const formatHeadline = (headline: string, activeIndex: number) => {
  const isPhxLocation = headline.includes('Pharmaprix');
  const headlineMap: Record<number, string> = {
    [TAB_STORE_DETAILS.PHARM_AND_SERVICES]: 'pharmacyHeading',
    [TAB_STORE_DETAILS.PHARM_CARE_CLINIC]: 'pharmacyCareClinicHeading',
    [TAB_STORE_DETAILS.STORE]: headline,
  };
  return isPhxLocation && activeIndex === TAB_STORE_DETAILS.PHARM_AND_SERVICES
    ? 'pharmaprixPharmacyOwner'
    : headlineMap[activeIndex];
};

const StoreDetails: React.FC<IStoreDetails> = ({ layout, storeId, isPhxPharmacyTab, setIsPhxPharmacyTab }) => {
  const { formatMessage, locale } = useIntl();
  const viewportType = useViewportType();
  const isDesktopView = viewportType === 'desktop';
  const {
    palette: { white },
    desktop: { desktopLarge02Bold },
    tablet: { tabletLarge02Bold },
    common: { common01Regular, common02Regular },
  } = useTheme();

  const {
    pharmacistInfo,
    storeDetails: {
      name,
      address1,
      city,
      province,
      postalCode,
      storeType,
      map: { latitude, longitude },
      flyerCTA,
      bookAnAppointmentCTA,
      storeIsTemporarilyClosed,
      clinicHours,
    },
  } = storeDetailsData(layout);
  const [distance, setDistance] = useState<string>('');

  const hasCareClinic = Boolean(clinicHours?.length);
  const isPhx = storeType === 'pharmaprix' || storeType === STORE_TYPES.PHX;
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    const isPhxAndPharmacyTab = storeType === 'PHX' && activeIndex === TAB_STORE_DETAILS.PHARM_AND_SERVICES;
    setIsPhxPharmacyTab(isPhxAndPharmacyTab);
  }, [activeIndex, setIsPhxPharmacyTab, storeType]);

  const [navigationItems, setNavigationItems] = useState(getInitialNavItems(formatMessage, storeType, hasCareClinic));

  const searchParams = useSearchParams();
  useEffect(() => {
    const tab = searchParams.get('tab') ? searchParams.get('tab') : '';
    const index = navigationItems.findIndex((item) => item.url === `/${tab}`);
    setActiveIndex(index);
  }, [searchParams]);

  const redirectToPage = useCallback((redirectUrl?: string) => {
    STORE_DETAILS_BREADCRUMBS[locale].forEach((crumb: any) => {
      if (redirectUrl && redirectUrl === crumb.url) {
        window.location.href = redirectUrl;
      }
    });
  }, []);

  const getPharmacyCTA = () => {
    if (storeType === STORE_TYPES.STTP_PSS && activeIndex === TAB_STORE_DETAILS.STORE) {
      return null;
    }

    const iconButton = (
      <IconButton
        icon={GoTagSvg as IconType}
        iconFill={white}
        fontWeight="700"
        linkUrl={flyerCTA}
        linkText={formatMessage({ id: 'viewStoreFlyer' })}
        rightIcon={true}
        variant="primary"
      />
    );

    const onBookAppointmentClick = () => (bookAnAppointmentCTA ? (window.location.href = bookAnAppointmentCTA) : null);
    const onRefillClick = () =>
      (window.location.href = isPhx
        ? formatMessage({ id: 'refillPrescriptionPHXCTA' })
        : formatMessage({ id: 'refillPrescriptionSDMCTA' }));

    const bookAndRefillButtons = (
      <>
        {bookAnAppointmentCTA && (
          <Button onClick={onBookAppointmentClick}>{formatMessage({ id: 'bookAppointment' })}</Button>
        )}
        <Button variant={'secondary'} onClick={onRefillClick}>
          {formatMessage({ id: 'refillPrescriptionOnline' })}
        </Button>
      </>
    );

    switch (activeIndex) {
      case 0: {
        return storeType === STORE_TYPES.SSP ? bookAndRefillButtons : flyerCTA ? iconButton : null;
      }
      case 1: {
        return bookAndRefillButtons;
      }
      default:
        break;
    }
  };

  const alertProps = {
    contentTitle: formatMessage({ id: 'locationTemporarilyClosed' }),
    contentBody1: <p>{formatMessage({ id: 'locationTemporarilyClosedBody' })}</p>,
    materialIcon: MdErrorOutline as any,
    variantId: 'ERROR' as const,
  };

  const tabHeadline = formatHeadline(capitalizeEveryFirstLetter(name), activeIndex);
  const storeLocation = getLocationName(capitalizeEveryFirstLetter(name));

  // If the store details info is a pharmaprix, it should show pharmacist name on the Pharmacy tab.
  const headlineText = formatMessage(
    { id: tabHeadline },
    { location: storeLocation, pharmacistName: capitalizeEveryFirstLetter(pharmacistInfo.name) }
  );

  const handleGeoLocationSuccess = async (location: GeolocationPosition) => {
    await google.maps.importLibrary('geometry');
    if (location?.coords) {
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        { lat: location.coords.latitude, lng: location.coords.longitude },
        {
          lat: Number(latitude),
          lng: Number(longitude),
        }
      );
      if (distance) {
        setDistance(`| ${formatDistance(distance)} km`);
      }
    }
  };

  const handleUseMyLocation = useCallback(() => {
    navigator.geolocation.getCurrentPosition(handleGeoLocationSuccess, handleGeoLocationError, geoLocationOptions);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      handleUseMyLocation();
    }
  }, []);

  const formattedAddress = `${capitalizeEveryFirstLetter(
    `${address1}, ${city}`
  )}, ${province} ${postalCode} ${distance}`;

  return (
    <APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string}>
      <WrapperSection>
        {<Breadcrumbs breadcrumbItems={STORE_DETAILS_BREADCRUMBS[locale]} buttonClickHandler={redirectToPage} />}
        {storeIsTemporarilyClosed && (
          <div style={{ marginTop: '4rem' }} data-testid="store-details-alert">
            <Alert {...alertProps} />
          </div>
        )}
        <div className={styles.headerContainer}>
          <Text sx={isDesktopView ? desktopLarge02Bold : tabletLarge02Bold} as="h1">
            {headlineText}
          </Text>
        </div>
        <Text sx={isDesktopView ? common01Regular : common02Regular}>{formattedAddress}</Text>
        <Text sx={isDesktopView ? common01Regular : common02Regular}>
          {formatMessage({ id: 'storeId' })}
          {storeId}
        </Text>
        <div className={styles.pharmacyCTA}>{getPharmacyCTA()}</div>

        <div className={styles.tabsContainer}>
          <TabComponent
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
            navigationItems={navigationItems}
            setNavigationItems={setNavigationItems}
            isUpdateStateRequired={true}
          />
        </div>
        {activeIndex === TAB_STORE_DETAILS.STORE ? (
          <>
            <StoreInformation layout={layout} activeIndex={activeIndex} />
            <BeautyBrands layout={layout} />
          </>
        ) : null}
        {activeIndex === TAB_STORE_DETAILS.PHARM_AND_SERVICES ? (
          <PharmacyAndServices layout={layout} storeType={storeType} storeId={storeId} isPhx={isPhx} />
        ) : null}
        {activeIndex === TAB_STORE_DETAILS.PHARM_CARE_CLINIC && hasCareClinic ? (
          <PharmacyCareClinic layout={layout} storeId={storeId} />
        ) : null}
      </WrapperSection>
    </APIProvider>
  );
};

export default StoreDetails;
